// `m8t platform verify-content` — INTERNAL. The engine's preflight gate.
//
// The installer image carries bytes (personas/, deploy/, brain-seeds/) and the
// manifest it was handed names tree SHAs for those same bytes. This command is
// where the two are compared, before any resource exists.
//
// It lives in the CLI rather than in installer/lib.sh because the brain-seed
// comparison is one opaque string with a specific format, and two
// implementations of that format already have to agree byte for byte
// (scripts/install-descriptor-gen.mjs writes it, lib/platform-converge.ts
// computes it from a manifest). A jq third copy in shell is the duplication this
// change exists to remove — so the engine shells out to the same code the
// converge rail already trusts.
import { readFileSync } from "node:fs";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { crossCheckContent, describeCrossCheckProblems, type DescriptorContent } from "../../lib/platform-content-crosscheck.js";
import { validateManifest, type ReleaseManifest } from "../../lib/release-manifest.js";

interface Descriptor {
  schemaVersion?: number;
  content?: Partial<DescriptorContent>;
}

function readJson(path: string, what: string): unknown {
  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_VERIFY_READ_FAILED", message: `Could not read the ${what} at '${path}': ${(e as Error).message}` });
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_VERIFY_PARSE_FAILED", message: `The ${what} at '${path}' is not valid JSON: ${(e as Error).message}` });
  }
}

export class PlatformVerifyContentCommand extends M8tCommand {
  static paths = [["platform", "verify-content"]];
  static usage = Command.Usage({
    category: "Platform",
    description: "INTERNAL: check that this installer image's baked content is what the release manifest names.",
    details:
      "Run by the cloud installer at preflight, before anything is deployed. Compares the image's install-descriptor.json content block (persona tree SHAs, the deploy/ tree SHA, the brain-seed rev — all computed from the tree the image actually baked) against the same facts in the release manifest the install was handed. Exits non-zero, naming every field that disagrees and both values, when they do not match. Not a founder-facing command.",
  });

  manifest = Option.String("--manifest", { description: "Path to the fetched, digest-verified release manifest." });
  descriptor = Option.String("--descriptor", { description: "Path to install-descriptor.json (baked into this image)." });

  // Not `async`: this command is pure file reads and a comparison, with no
  // awaited work at all. The base class wants a Promise, so it gets one.
  executeCommand(): Promise<number> {
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_VERIFY_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };

    const manifestPath = need(this.manifest, "--manifest");
    const rawManifest = readJson(manifestPath, "release manifest");
    // SCHEMA-VALIDATE BEFORE READING FIELDS. The engine's own shell-side check
    // (manifest_schema_ok) only inspects schemaVersion and the gateway digest, so
    // a document that satisfies it can still be missing components.personas —
    // where crossCheckContent would throw a raw TypeError that the entrypoint
    // reports as "this image does not carry the content that platform X names".
    // That is a confident wrong diagnosis, which is worse than the crash.
    const manifestErrors = validateManifest(rawManifest);
    if (manifestErrors.length > 0) {
      throw new LocalCliError({
        code: "PLATFORM_VERIFY_MANIFEST_INVALID",
        message: `The release manifest at '${manifestPath}' is not a manifest this platform can read:\n  - ${manifestErrors.join("\n  - ")}`,
        hint: "Nothing was compared and nothing was deployed. This is a fault in the release, not in your setup.",
      });
    }
    const manifest = rawManifest as ReleaseManifest;
    const descriptor = readJson(need(this.descriptor, "--descriptor"), "install descriptor") as Descriptor;

    // A descriptor with no content block claims nothing, and an image that claims
    // nothing about its own bytes cannot be checked against anything. That is a
    // defective image, not a pass: the generator emits the block for every build
    // that can see its own tree, and refuses the build outright when it cannot.
    // Treating "nothing to compare" as agreement is how a check stops being one.
    const content = descriptor.content;
    if (!content || typeof content.brainSeedsRev !== "string" || typeof content.infra !== "string" || typeof content.personas !== "object") {
      throw new LocalCliError({
        code: "PLATFORM_VERIFY_DESCRIPTOR_INCOMPLETE",
        message:
          "This installer image's descriptor carries no usable content block, so there is nothing to check the release manifest against.",
        hint:
          "The image was built without scripts/install-descriptor-gen.mjs, or built where it could not read its own tree. " +
          "It is malformed — reinstall with the current CLI (`npm install -g @m8t-stack/cli`).",
      });
    }

    const problems = crossCheckContent(manifest, {
      personas: content.personas,
      infra: content.infra,
      brainSeedsRev: content.brainSeedsRev,
    });
    if (problems.length > 0) {
      throw new LocalCliError({
        code: "PLATFORM_VERIFY_CONTENT_MISMATCH",
        message: describeCrossCheckProblems(problems, manifest.platform.version),
      });
    }

    this.context.stderr.write(`content verified against platform ${manifest.platform.version}\n`);
    return Promise.resolve(0);
  }
}
